import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';

// Layouts
import AdminLayout    from './layouts/AdminLayout';
import CustomerLayout from './layouts/CustomerLayout';

// Auth pages
import Login               from './pages/Login';
import Register            from './pages/Register';
import RegistrationPending from './pages/RegistrationPending';

// Admin pages
import AdminDashboard    from './pages/admin/AdminDashboard';
import CustomerList      from './pages/admin/CustomerList';
import CustomerCreate    from './pages/admin/CustomerCreate';
import CustomerDetail    from './pages/admin/CustomerDetail';
import OrderList         from './pages/admin/OrderList';
import OrderDetail       from './pages/admin/OrderDetail';
import CreateOrder       from './pages/admin/CreateOrder';
import ProductPricingList from './pages/admin/ProductPricingList';
import FeaturedProducts   from './pages/admin/FeaturedProducts';
import ReportsDashboard  from './pages/admin/ReportsDashboard';
import Settings          from './pages/admin/Settings';
import AllFollowUps      from './pages/admin/crm/AllFollowUps';
import QBOSettings       from './pages/admin/QBOSettings';
import AgentQueue        from './pages/admin/agent/AgentQueue';
import AgentTicketDetail from './pages/admin/agent/AgentTicketDetail';
import AgentMonitoring   from './pages/admin/agent/AgentMonitoring';

// Customer pages
import StorefrontHome      from './pages/customer/StorefrontHome';
import AccountDashboard    from './pages/customer/AccountDashboard';
import Catalog             from './pages/customer/Catalog';
import ProductDetail       from './pages/customer/ProductDetail';
import Checkout            from './pages/customer/Checkout';
import OrderHistory        from './pages/customer/OrderHistory';
import CustomerOrderDetail from './pages/customer/CustomerOrderDetail';
import CustomerProfile     from './pages/customer/CustomerProfile';

// ── Protected route wrapper ────────────────────────────────────────────────────
function RequireAuth({ children, roles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (roles && !roles.includes(user.role)) {
    if (['admin', 'sales_rep'].includes(user.role)) {
      return <Navigate to="/admin/customers" replace />;
    }
    return <Navigate to="/wholesale/home" replace />;
  }

  return children;
}

// ── App ────────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login"                element={<Login />} />
          <Route path="/register"             element={<Register />} />
          <Route path="/registration-pending" element={<RegistrationPending />} />

          {/* Admin — sidebar layout */}
          <Route
            path="/admin"
            element={
              <RequireAuth roles={['admin', 'sales_rep', 'customer_service']}>
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route index                    element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard"         element={<AdminDashboard />} />
            <Route path="customers"         element={<CustomerList />} />
            <Route path="customers/new"     element={<CustomerCreate />} />
            <Route path="customers/:id"     element={<CustomerDetail />} />
            <Route path="orders"            element={<OrderList />} />
            <Route path="orders/create"     element={<CreateOrder />} />
            <Route path="orders/:id"        element={<OrderDetail />} />
            <Route path="products"          element={<ProductPricingList />} />
            <Route path="products/featured" element={<FeaturedProducts />} />
            <Route path="reports"           element={<ReportsDashboard />} />
            <Route path="settings"          element={<Settings />} />
            <Route path="crm/follow-ups"        element={<AllFollowUps />} />
            <Route path="agent/queue"            element={<AgentQueue />} />
            <Route path="agent/tickets/:id"      element={<AgentTicketDetail />} />
            <Route path="agent/monitoring"       element={<RequireAuth roles={['admin']}><AgentMonitoring /></RequireAuth>} />
            <Route path="qbo"                    element={<QBOSettings />} />
          </Route>

          {/* Customer — top nav layout */}
          <Route
            path="/wholesale"
            element={
              <RequireAuth roles={['wholesale', 'admin', 'sales_rep']}>
                <CartProvider>
                  <CustomerLayout />
                </CartProvider>
              </RequireAuth>
            }
          >
            <Route index                 element={<Navigate to="/wholesale/home" replace />} />
            <Route path="home"           element={<StorefrontHome />} />
            <Route path="account"        element={<AccountDashboard />} />
            <Route path="catalog"        element={<Catalog />} />
            <Route path="products/:id"   element={<ProductDetail />} />
            <Route path="checkout"       element={<Checkout />} />
            <Route path="orders"         element={<OrderHistory />} />
            <Route path="orders/:id"     element={<CustomerOrderDetail />} />
            <Route path="profile"        element={<CustomerProfile />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
